import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Search, RefreshCw, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { YamlSourceBadge } from '@/components/YamlSourceBadge';

export default function CustomersPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (customerId) => {
    if (!window.confirm(`Are you sure you want to delete customer ${customerId}?`)) {
      return;
    }
    try {
      const api = authAxios();
      await api.delete(`/customers/${customerId}`);
      toast.success(`Customer ${customerId} deleted`);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete customer');
    }
  };

  const filtered = useMemo(() => {
    return customers.filter(c => {
      const q = search.toLowerCase();
      return !q ||
        (c.name || '').toLowerCase().includes(q) ||
        (c.id || '').toLowerCase().includes(q);
    });
  }, [customers, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
            Customers
          </h1>
          <YamlSourceBadge className="text-xs" />
        </div>
        <p className="text-sm text-[#a1a1aa] mt-1">
          {customers.length} active customers
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
          <Input
            placeholder="Search customers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-11 pl-10 bg-[#121214] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
          />
        </div>
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa]">Name</TableHead>
                <TableHead className="text-[#a1a1aa]">Type</TableHead>
                <TableHead className="text-[#a1a1aa]">Status</TableHead>
                <TableHead className="text-[#a1a1aa]">Domains</TableHead>
                <TableHead className="text-[#a1a1aa]">Contacts</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((cust) => (
                <TableRow 
                  key={cust.id} 
                  className="border-white/5 hover:bg-white/[0.03] cursor-pointer"
                  onClick={() => {
                    console.log('Navigating to', `/customers/${cust.id}`);
                    navigate(`/customers/${cust.id}`);
                  }}
                >
                  <TableCell className="font-medium text-white" style={{ pointerEvents: 'none' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#EF694C]/10 flex items-center justify-center text-[#EF694C]">
                        <Users className="w-4 h-4" />
                      </div>
                      <div>
                        <div>{cust.name}</div>
                        <div className="text-xs text-[#52525b] font-mono">{cust.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell style={{ pointerEvents: 'none' }}>
                    <Badge variant="outline" className="border-white/10 text-[#a1a1aa]">
                      {cust.type}
                    </Badge>
                  </TableCell>
                  <TableCell style={{ pointerEvents: 'none' }}>
                    <Badge className={cust.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-gray-500/10 text-gray-500'}>
                      {cust.status}
                    </Badge>
                  </TableCell>
                  <TableCell style={{ pointerEvents: 'none' }}>
                    <Badge variant="secondary" className="bg-white/5 text-white">
                      {cust.domain_count || 0}
                    </Badge>
                  </TableCell>
                  <TableCell style={{ pointerEvents: 'none' }}>
                    <div className="flex flex-col gap-1">
                      {cust.contacts?.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-[#a1a1aa]">
                          <Mail className="w-3 h-3" />
                          <span>{c.email}</span>
                          {c.role && <span className="text-xs text-[#52525b]">({c.role})</span>}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500/50 hover:text-red-500 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(cust.id);
                      }}
                      title="Delete Customer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
